import React, { useEffect, useRef, useState } from "react";
import { createParticle, petalCount, stepParticle } from "../../lib/petals";

const MAX_PIXEL_RATIO = 2;
const MAX_FRAME_SECONDS = 0.05;
const SPARK_RGB = "255, 163, 74";

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Sprites are painted once, so each frame is only a handful of drawImage calls.
const makeSprite = (size, paint) => {
  const sprite = document.createElement("canvas");
  sprite.width = size;
  sprite.height = size;
  paint(sprite.getContext("2d"), size);
  return sprite;
};

// Same colours as the petals and sparks in the home portrait scene.
const paintSpark = (ctx, size) => {
  const glow = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  glow.addColorStop(0, `rgba(${SPARK_RGB}, 1)`);
  glow.addColorStop(0.35, `rgba(${SPARK_RGB}, 0.45)`);
  glow.addColorStop(1, `rgba(${SPARK_RGB}, 0)`);
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, size, size);
};

const paintPetal = (ctx, size) => {
  const fill = ctx.createLinearGradient(0, size, size, 0);
  fill.addColorStop(0, "#c7300a");
  fill.addColorStop(0.6, "#ff9f2e");
  fill.addColorStop(1, "#ffd27a");
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.ellipse(size / 2, size / 2, size * 0.42, size * 0.2, 0, 0, Math.PI * 2);
  ctx.fill();
};

/** Starts the animation on `canvas`; returns a cleanup function. */
function startPetals(canvas, ctx) {
  const sprites = { spark: makeSprite(32, paintSpark), petal: makeSprite(48, paintPetal) };
  let width = 0;
  let height = 0;
  let particles = [];
  let pointer = null;
  let frameId = 0;
  let time = 0;
  let lastFrame = performance.now();

  const resize = () => {
    width = window.innerWidth;
    height = window.innerHeight;
    const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    const count = petalCount(width, height);
    const added = Array.from({ length: Math.max(0, count - particles.length) }, () => createParticle(width, height));
    particles = particles.slice(0, count).concat(added);
  };

  const draw = () => {
    ctx.clearRect(0, 0, width, height);
    particles.forEach((particle) => {
      const sprite = particle.isPetal ? sprites.petal : sprites.spark;
      ctx.save();
      ctx.globalAlpha = particle.alpha;
      ctx.translate(particle.x, particle.y);
      ctx.rotate(particle.angle);
      ctx.drawImage(sprite, -particle.size / 2, -particle.size / 2, particle.size, particle.size);
      ctx.restore();
    });
  };

  const loop = (now) => {
    const dt = Math.min((now - lastFrame) / 1000, MAX_FRAME_SECONDS);
    lastFrame = now;
    time += dt;
    particles = particles.map((particle) => stepParticle(particle, { dt, time, width, height, pointer }));
    draw();
    frameId = requestAnimationFrame(loop);
  };

  const onPointerMove = (event) => {
    pointer = { x: event.clientX, y: event.clientY };
  };
  const onPointerLeave = () => {
    pointer = null;
  };
  const onVisibilityChange = () => {
    lastFrame = performance.now();
  };

  resize();
  window.addEventListener("resize", resize);
  window.addEventListener("pointermove", onPointerMove, { passive: true });
  document.documentElement.addEventListener("pointerleave", onPointerLeave);
  document.addEventListener("visibilitychange", onVisibilityChange);
  frameId = requestAnimationFrame(loop);

  return () => {
    cancelAnimationFrame(frameId);
    window.removeEventListener("resize", resize);
    window.removeEventListener("pointermove", onPointerMove);
    document.documentElement.removeEventListener("pointerleave", onPointerLeave);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}

/**
 * Site-wide layer of drifting marigold petals behind the page content. It never catches clicks, is skipped for
 * visitors who prefer reduced motion and stays idle where no 2D canvas is available.
 */
const PetalField = () => {
  const canvasRef = useRef(null);
  const [isEnabled] = useState(() => !prefersReducedMotion());

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return undefined;
    return startPetals(canvas, ctx);
  }, []);

  if (!isEnabled) return null;
  return <canvas ref={canvasRef} className="petal-field" aria-hidden="true" />;
};

export default PetalField;
